import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Breadcrumbs, Button, IconButton, MenuItem, Select, TextField, Tooltip, Typography } from '@material-ui/core';
import AssignmentIcon from '@material-ui/icons/Assignment';
import DescriptionIcon from '@material-ui/icons/Description';
import InfoIcon from '@material-ui/icons/Info';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { Controller, FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import instance, { safeGet } from '../../axios-default';
import { easySnackbar } from '../../store/Noti/noti.actions';
import { useLang } from '../../util/lang';
import { smilDesignQuestionsConfig, smilDesignQuestionsConfigFirstFit } from '../SmileDesign/SmileDesignQuestions';
import PercentageProgressRing from '../../components/PercentageProgressRing';
import RadioOption from '../../components/FormOptions/RadioOption';
import CheckboxOption from '../../components/FormOptions/CheckboxOption';
import AdditionalSyringeSelector from '../../components/AdditionalSyringeSelector';
import FitShadeSelector from '../../components/FitShadeSelector';
import { preloadShadeImages } from '../../components/shadeImages';
import RestorativeOptionalSection from '../../components/RestorativePhase/RestorativeOptionalSection';
import newPatientButtonStyle from '../../components/NewPatient/NewPatientButtonTabs.module.css';
import newPatientTabsStyle from '../../components/NewPatient/NewPatientsTabs.module.css';
import ErrorDialog from '../../components/NewPatient/ErrorDialog/ErrorDialog';
import ConfirmDialog from '../../components/Dialog/ConfirmDialog';
import { dialogData, setDialog, setDialogMessage } from '../../store/ErrorMessage/ErrorMessageSlice';
import { fetchConnectPlatformData } from '../../store/ConnectPlatform/connectPlatform';
import styles from '../RestorativePhaseCaseIntake.module.css';
import MockupFeedbackStep from './MockupFeedbackStep';
import PrescriptionNoteStep from './PrescriptionNoteStep';
import ImpressionStep from './ImpressionStep';

const STEPS = [
    { id: 'mockup', labelKey: '@PatientInfo.smileDesign.restorativePhase.mockupStep', Icon: AssignmentIcon },
    {
        id: 'prescription',
        labelKey: '@PatientInfo.smileDesign.restorativePhase.prescriptionStep',
        Icon: DescriptionIcon,
    },
    { id: 'impression', labelKey: '@PatientInfo.smileDesign.restorativePhase.impressionStep', Icon: AssignmentIcon },
];
const FIT_VALUES = ['option.formaInjectionTechnique', 'option.flowableInjectionTechnique'];
const PRESCRIPTION_DETAIL_FIELD_NAMES = [
    // 'orthodonticPhase',
    // 'fitType',
    'injectionLayers',
    'nanoFilledInjectableCompositeShade',
    'additionalSyringesIncreasedOpacity',
    'materialPreference',
    'subMaterialPreference',
    'specifiedMaterialIfAny',
    'shadeGuide',
    'targetShade',
    'otherShade',
    'currentShade',
    'materialSelection',
    'translucencyLevel',
    'surfaceTexture',
    'surfaceFinishingCharacterization',
    'effectsToReproduce',
    'cementationTray',
    'designSpecifications',
    'optionalAppliances',
    'restorationRemark',
];
const SOLUTIONS_WITH_FINISHING_DEFAULTS = ['option.ceramicVeneersCrowns', 'option.guidedVeneerTreatment'];
const FINISHING_QUESTION_DEFAULTS = {
    translucencyLevel: 'medium',
    surfaceTexture: 'medium',
    surfaceFinishingCharacterization: 'fullGlazing',
};
const TARGET_SHADE_OPTIONS = [
    'A1','A2','A3','A3.5','A4','B1','B2','B3','B4','C1','C2','C3','C4','D2','D3','D4','BL1', 'BL2', 'BL3', 'BL4','OM1', 'OM2', 'OM3'
];
const IPS_LIDISI_SHADE_OPTIONS = [
    'A1',
    'A2',
    'A3',
    'A3.5',
    'A4',
    'B1',
    'B2',
    'B3',
    'B4',
    'C1',
    'C2',
    'C3',
    'C4',
    'D2',
    'D3',
    'D4',
    'BL1',
    'BL2',
    'BL3',
    'BL4',
];
const VITA_SHADE_GROUPS = [
    {
        helperKey: '@PatientInfo.smileDesign.restorativePhase.vitaClassicShadeGuide',
        options: ['A1','A2','A3','A3.5','A4','B1','B2','B3','B4','C1','C2','C3','C4','D2','D3','D4'],
    },
    {
        helperKey: '@PatientInfo.smileDesign.restorativePhase.vitaBleachedShadeGuide',
        options: ['OM1', 'OM2', 'OM3'],
    },
    {
        helperKey: '@PatientInfo.smileDesign.restorativePhase.ipsEmaxBleachShadeGuide',
        options: ['BL1', 'BL2', 'BL3', 'BL4'],
    },
];
const VITA_GUIDE_SHADE_OPTIONS = [
    'A1', 'A2', 'A3', 'A3.5', 'A4', 'B1', 'B2', 'B3', 'B4', 'C1', 'C2', 'C3', 'C4', 'D2', 'D3', 'D4',
    'OM1', 'OM2', 'OM3', '1M1', '1M2', '2L1.5', '2M1', '2M2', '2M3', '2R1.5', '2R2.5', '3L1.5', '3L2.5',
    '3M1', '3M2', '3M3',
];
const VITA_GUIDE_SHADE_GROUPS = [
    {
        helperKey: '@PatientInfo.smileDesign.restorativePhase.vitaClassicShadeGuide',
        options: ['A1', 'A2', 'A3', 'A3.5', 'A4', 'B1', 'B2', 'B3', 'B4', 'C1', 'C2', 'C3', 'C4', 'D2', 'D3', 'D4'],
    },
    {
        helperKey: '@PatientInfo.smileDesign.restorativePhase.vitaBleachedShadeGuide',
        options: ['OM1', 'OM2', 'OM3'],
    },
    {
        helperKey: '@PatientInfo.smileDesign.restorativePhase.vitaSystem3dMasterShadeGuide',
        options: ['1M1', '1M2', '2L1.5', '2M1', '2M2', '2M3', '2R1.5', '2R2.5', '3L1.5', '3L2.5', '3M1', '3M2', '3M3'],
    },
];
const IPS_EMAX_GUIDE_SHADE_OPTIONS = [
    'A1', 'A2', 'A3', 'A3.5', 'A4', 'B1', 'B2', 'B3', 'B4', 'C1', 'C2', 'C3', 'C4', 'D2', 'D3', 'D4',
    'BL1', 'BL2', 'BL3', 'BL4',
];
const IPS_EMAX_GUIDE_SHADE_GROUPS = [
    {
        helperKey: '@PatientInfo.smileDesign.restorativePhase.vitaClassicShadeGuide',
        options: ['A1', 'A2', 'A3', 'A3.5', 'A4', 'B1', 'B2', 'B3', 'B4', 'C1', 'C2', 'C3', 'C4', 'D2', 'D3', 'D4'],
    },
    {
        helperKey: '@PatientInfo.smileDesign.restorativePhase.ipsEmaxBleachShadeGuide',
        options: ['BL1', 'BL2', 'BL3', 'BL4'],
    },
];
const FIT_APPLIANCES = [
    'Bleaching retainer',
    'Ecodont retainer',
    'Ecodont combi retainer',
    'Invisible retainer',
    'Ecodont® Retainer with Veneer Shield',
    'Splint',
];

/** Keeps existing field error placeholders without rendering inline validation text. */
const FieldError = () => null;

/** Renders a selectable row of radio option cards with an optional default value. */
const RadioCardGroup = ({ name, options, required = false, card = true, defaultValue, onValueChange, className = '' }) => {
    const { control } = useFormContext();
    return (
        <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={required ? { required: 'Required' } : undefined}
            render={({ field }) => (
                <div className={`${card ? 'restorative-card-grid' : 'restorative-inline-options'} ${className}`}>
                    {options.map((option) => (
                        <RadioOption
                            key={option.value}
                            {...option}
                            className={card ? 'restorative-option-card' : 'restorative-option-inline'}
                            selected={field.value === option.value}
                            onChange={() => (onValueChange ? onValueChange(option.value) : field.onChange(option.value))}
                        />
                    ))}
                </div>
            )}
        />
    );
};

/** Renders a selectable group of checkbox options. */
const CheckboxGroup = ({ name, options, required = false, card = true }) => {
    const { control, watch } = useFormContext();
    const selectedValues = watch(name) || [];
    return (
        <Controller
            name={name}
            control={control}
            defaultValue={[]}
            rules={required ? { validate: (value) => value?.length > 0 || 'Required' } : undefined}
            render={({ field }) => (
                <div className={card ? 'restorative-card-grid' : 'restorative-checkbox-list'}>
                    {options.map((option) => (
                        <CheckboxOption
                            key={option.value}
                            {...option}
                            className={card ? 'restorative-checkbox-card' : 'restorative-checkbox-inline'}
                            selectedClassName="restorative-checkbox-selected"
                            checked={selectedValues.includes(option.value)}
                            onChange={(event) => {
                                const value = event.target.checked
                                    ? [...selectedValues, option.value]
                                    : selectedValues.filter((selected) => selected !== option.value);
                                field.onChange(value);
                            }}
                        />
                    ))}
                </div>
            )}
        />
    );
};

/** Renders an optionally grouped shade chip selector backed by a single or multiple field value. */
const ShadeSelector = ({ name, options = TARGET_SHADE_OPTIONS, optionGroups, required = false, multiple = false }) => {
    const { control } = useFormContext();
    const get = useLang();
    return (
        <Controller
            name={name}
            control={control}
            defaultValue={multiple ? [] : ''}
            rules={required ? { validate: (value) => value?.length > 0 || 'Required' } : undefined}
            render={({ field }) => {
                const selectedValues = multiple ? (Array.isArray(field.value) ? field.value : []) : [field.value || ''];
                const groupedOptions = optionGroups
                    ?.map((group) => ({ ...group, options: group.options.filter((value) => options.includes(value)) }))
                    .filter((group) => group.options.length > 0);
                const ungroupedOptions = groupedOptions
                    ? options.filter((value) => !groupedOptions.some((group) => group.options.includes(value)))
                    : options;
                /** Renders one selectable shade option. */
                const renderOption = (value) => (
                    <button
                        key={value}
                        type="button"
                        className={`restorative-shade-chip${selectedValues.includes(value) ? ' restorative-shade-chip-selected' : ''}`}
                        onClick={() =>
                            field.onChange(
                                multiple
                                    ? selectedValues.includes(value)
                                        ? selectedValues.filter((selectedValue) => selectedValue !== value)
                                        : [...selectedValues, value]
                                    : value
                            )
                        }
                    >
                        {value}
                    </button>
                );
                return (
                    <div className="restorative-shade-selector">
                        {groupedOptions?.map((group) => (
                            <div className="restorative-shade-group" style={{marginLeft:"8px"}} key={group.helperKey}>
                                <div className="restorative-shade-group-title">
                                    <span>{get(group.helperKey)}</span>
                                </div>
                                <div className="restorative-shade-options">{group.options.map(renderOption)}</div>
                            </div>
                        ))}
                        {ungroupedOptions.length > 0 && <div className="restorative-shade-options">{ungroupedOptions.map(renderOption)}</div>}
                    </div>
                );
            }}
        />
    );
};

/** Renders a reusable text field backed by React Hook Form. */
const RestorativeTextField = ({ name, placeholderKey, required = false }) => {
    const get = useLang();
    const { control } = useFormContext();
    return (
        <Controller
            name={name}
            control={control}
            defaultValue=""
            rules={required ? { required: 'Required' } : undefined}
            render={({ field }) => (
                <TextField
                    {...field}
                    className="restorative-input"
                    placeholder={placeholderKey ? get(placeholderKey) : undefined}
                    variant="outlined"
                />
            )}
        />
    );
};

/** Renders a reusable select field backed by React Hook Form. */
const RestorativeSelect = ({ name, options, required = false }) => {
    const get = useLang();
    const { control } = useFormContext();
    return (
        <Controller
            name={name}
            control={control}
            defaultValue=""
            rules={required ? { required: 'Required' } : undefined}
            render={({ field }) => (
                <Select {...field} displayEmpty className={`restorative-select${field.value === '' || field.value === null || field.value === undefined ? ' select-empty-value' : ''}`} variant="outlined">
                    <MenuItem value="">{get('@PatientInfo.smileDesign.restorativePhase.selectShade')}</MenuItem>
                    {options.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            )}
        />
    );
};

/** Renders all F.I.T. restoration detail fields. */
const FitDetails = ({ get, plannedRestorationSolution }) => {
    const { setValue, watch } = useFormContext();
    const injectionLayers = watch('injectionLayers');

    /** Updates the injection layer count and clears incompatible shade selections. */
    const handleInjectionLayerChange = (value) => {
        setValue('injectionLayers', value);
        setValue('nanoFilledInjectableCompositeShade', []);
    };

    return (
        <>
        <div className="restorative-section-heading">{get('@PatientInfo.smileDesign.restorativePhase.sectionTwo')}</div>
        <div className="restorative-product-heading">
            F.I.T. — {get(
                plannedRestorationSolution === 'option.formaInjectionTechnique'
                    ? '@PatientInfo.smileDesign.prescriptionNotes.option.formaInjectionTechnique'
                    : '@PatientInfo.smileDesign.prescriptionNotes.option.flowableInjectionTechnique'
            )}
        </div>
        <div className="restorative-product-description">{get('@PatientInfo.smileDesign.restorativePhase.fitSubDesc')}</div>
        <div className="restorative-question-title">
            2. {get('@PatientInfo.smileDesign.restorativePhase.injectionLayers')} <b>*</b>
        </div>
        <RadioCardGroup
            name="injectionLayers"
            required
            className="restorative-injection-layer-options"
            onValueChange={handleInjectionLayerChange}
            options={[
                {
                    value: 'oneLayer',
                    label: (
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            {get('@PatientInfo.smileDesign.restorativePhase.options.oneLayer')}
                            <Tooltip classes={{ tooltip: 'restorative-tooltip' }} style={{fontSize:"28px"}} title={get('@PatientInfo.smileDesign.restorativePhase.options.oneLayerTooltip')} arrow>
                                <InfoIcon color="secondary" />
                            </Tooltip>
                        </span>
                    ),
                    description: get('@PatientInfo.smileDesign.restorativePhase.options.oneLayerDescription'),
                },
                {
                    value: 'twoLayers',
                    label: (
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            {get('@PatientInfo.smileDesign.restorativePhase.options.twoLayers')}
                            <Tooltip classes={{ tooltip: 'restorative-tooltip' }} style={{fontSize:"28px"}} title={get('@PatientInfo.smileDesign.restorativePhase.options.twoLayersTooltip')} arrow>
                                <InfoIcon color="secondary" />
                            </Tooltip>
                        </span>
                    ),
                    description: get('@PatientInfo.smileDesign.restorativePhase.options.twoLayersDescription'),
                },
            ]}
        />
        <FieldError name="injectionLayers" />
        <div className="restorative-question-title">
            3. {get('@PatientInfo.smileDesign.restorativePhase.fitShade')} <b>*</b>
        </div>
        {injectionLayers ? (
            <>
                <FitShadeSelector name="nanoFilledInjectableCompositeShade" required />
                <FieldError name="nanoFilledInjectableCompositeShade" />
            </>
        ) : (
            <div className="restorative-hint">
                {get('@PatientInfo.smileDesign.restorativePhase.shadeHint')}
            </div>
        )}
        <AdditionalSyringeSelector
            name="additionalSyringesIncreasedOpacity"
            title={
                <>
                    4. {get('@PatientInfo.smileDesign.restorativePhase.additionalSyringes')}{' '}
                    <span>({get('@PatientInfo.smileDesign.restorativePhase.additionalSyringesOptional')})</span>
                </>
            }
        />
        <RestorativeOptionalSection
            appliances={FIT_APPLIANCES.map((value) => ({
                value,
                label: get(`@PatientInfo.smileDesign.restorativePhase.options.${{
                    'Bleaching retainer': 'bleachingRetainer',
                    'Ecodont combi retainer': 'ecodontCombiRetainer',
                    'Ecodont® Retainer with Veneer Shield': 'veneerRetainer',
                    'Veneer retainer': 'veneerRetainer',
                    'Ecodont retainer': 'ecodontRetainer',
                    'Invisible retainer': 'invisibleRetainer',
                    Splint: 'splint',
                }[value]}`),
            }))}
            startNumber={5}
        />
        </>
    );
};

/** Renders all Ceramic Veneers restoration detail fields. */
const CeramicDetails = ({ get }) => {
    const { control, setValue, watch } = useFormContext();
    const materialPreference = watch('materialPreference');
    const subMaterialPreference = watch('subMaterialPreference');
    const shadeGuide = watch('shadeGuide');
    const isZirconia = materialPreference === 'zirconia';
    const isEzneerGuide = shadeGuide === 'ezneer';
    const isVitaGuide = shadeGuide === 'vita';
    const previousMaterialPreference = useRef(materialPreference);
    const previousShadeGuide = useRef(shadeGuide);
    const surfaceFinishing = watch('surfaceFinishingCharacterization');

    /** Clears dependent selections when the selected material category changes. */
    useEffect(() => {
        if (previousMaterialPreference.current && previousMaterialPreference.current !== materialPreference) {
            setValue('subMaterialPreference', '');
            setValue('specifiedMaterialIfAny', '');
            setValue('shadeGuide', '');
            setValue('currentShade', '');
            setValue('targetShade', '');
            setValue('otherShade', '');
            setValue('materialSelection', '');
        }
        previousMaterialPreference.current = materialPreference;
    }, [materialPreference, setValue]);

    /** Clears the custom material note when a listed option is selected. */
    useEffect(() => {
        if (subMaterialPreference && !['otherZirconia', 'otherLithiumDisilicate'].includes(subMaterialPreference)) {
            setValue('specifiedMaterialIfAny', '');
        }
    }, [setValue, subMaterialPreference]);

    /** Clears shade values when a different shade guide is selected. */
    useEffect(() => {
        if (previousShadeGuide.current && previousShadeGuide.current !== shadeGuide) {
            setValue('currentShade', '');
            setValue('targetShade', '');
            setValue('otherShade', '');
            setValue('materialSelection', '');
        }
        previousShadeGuide.current = shadeGuide;
    }, [setValue, shadeGuide]);

    /** Clears effects when staining and glazing is no longer selected. */
    useEffect(() => {
        if (surfaceFinishing !== 'stainingGlazing') setValue('effectsToReproduce', []);
    }, [setValue, surfaceFinishing]);
    return (
        <>
            <div className="restorative-section-heading">
                {get('@PatientInfo.smileDesign.restorativePhase.sectionTwo')}
            </div>
            <div className="restorative-product-heading">
                {get('@PatientInfo.smileDesign.restorativePhase.ceramicVeneersManualDesc')}
            </div>
            <div className="restorative-question-title">
                2. {get('@PatientInfo.smileDesign.restorativePhase.materialPreference')} <b>*</b>
            </div>
            <RadioCardGroup
                name="materialPreference"
                required
                options={[
                    {
                        value: 'zirconia',
                        label: get('@PatientInfo.smileDesign.restorativePhase.options.zirconia'),
                    },
                    {
                        value: 'lithiumDisilicate',
                        label: get('@PatientInfo.smileDesign.restorativePhase.options.lithiumDisilicate'),
                    },
                ]}
            />
            <FieldError name="materialPreference" />
            {materialPreference && (
                <>
                    <RadioCardGroup
                        name="subMaterialPreference"
                        required
                        card={false}
                        className="restorative-material-option-list"
                        options={
                            isZirconia
                                ? [
                                      { value: 'zirneer', label: get('@PatientInfo.smileDesign.restorativePhase.options.zirneer') },
                                      { value: 'ezneer', label: get('@PatientInfo.smileDesign.restorativePhase.options.ezneer') },
                                      { value: 'threeDPrintedVeneer', label: get('@PatientInfo.smileDesign.restorativePhase.options.threeDPrintedVeneer') },
                                      { value: 'otherZirconia', label: get('@PatientInfo.smileDesign.restorativePhase.options.others') },
                                  ]
                                : [
                                      { value: 'ipsEmaxCad', label: get('@PatientInfo.smileDesign.restorativePhase.options.ipsEmaxCad') },
                                      { value: 'econLithiumDisilicate', label: get('@PatientInfo.smileDesign.restorativePhase.options.econLithiumDisilicate') },
                                      { value: 'otherLithiumDisilicate', label: get('@PatientInfo.smileDesign.restorativePhase.options.others') },
                                  ]
                        }
                    />
                    <FieldError name="subMaterialPreference" />
                    {['otherZirconia', 'otherLithiumDisilicate'].includes(subMaterialPreference) && (
                        <Controller
                            name="specifiedMaterialIfAny"
                            control={control}
                            defaultValue=""
                            rules={{ required: 'Required' }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    className="restorative-textarea"
                                    placeholder={get('@PatientInfo.smileDesign.restorativePhase.specifyOtherOption')}
                                    multiline
                                    rows={4}
                                    variant="outlined"
                                />
                            )}
                        />
                    )}
                </>
            )}
            <div className="restorative-question-title">
                3. {get('@PatientInfo.smileDesign.restorativePhase.shade')} <b>*</b>
            </div>
            {materialPreference ? (
                <>
                    <div style={{marginBottom:5}}>
                        <i>
                            {get('@PatientInfo.smileDesign.restorativePhase.whenOptionSelected').replace(
                                '{option}',
                                get(
                                    `@PatientInfo.smileDesign.restorativePhase.options.${isZirconia ? 'zirconia' : 'lithiumDisilicate'}`
                                ).toUpperCase()
                            )}
                        </i>
                    </div>
                    <RadioCardGroup
                        name="shadeGuide"
                        required
                        options={
                            isZirconia
                                ? [
                                      { value: 'vita', label: get('@PatientInfo.smileDesign.restorativePhase.vitaShadeGuide') },
                                      { value: 'ezneer', label: get('@PatientInfo.smileDesign.restorativePhase.ezneerShadeGuide') },
                                  ]
                                : [
                                      { value: 'vita', label: get('@PatientInfo.smileDesign.restorativePhase.vitaShadeGuide') },
                                      { value: 'ipsEmax', label: get('@PatientInfo.smileDesign.restorativePhase.ipsEmaxShadeGuide') },
                                  ]
                        }
                    />
                    <FieldError name="shadeGuide" />
                </>
            ) : (
                <div className="restorative-hint">
                    {get('@PatientInfo.smileDesign.restorativePhase.shadeHint')}
                </div>
            )}
            {isEzneerGuide && (
                <div className="restorative-shade-guide">
                    <div className="restorative-subquestion restorative-shade-label">
                        {get('@PatientInfo.smileDesign.restorativePhase.prepStumpShade')}
                    </div>
                    <RestorativeTextField
                        name="currentShade"
                        placeholderKey="@PatientInfo.smileDesign.restorativePhase.shadeInputPlaceholder"
                        required
                    />
                    <FieldError name="currentShade" />
                    <div className="restorative-subquestion restorative-shade-label">
                        {get('@PatientInfo.smileDesign.restorativePhase.targetShade')}
                    </div>
                    <ShadeSelector
                        name="targetShade"
                        options={['A1', 'A2', 'A3', 'BL1', 'BL2', 'BL3', 'BL4']}
                        required
                    />
                    <FieldError name="targetShade" />
                    <div className="restorative-subquestion">
                        {get('@PatientInfo.smileDesign.restorativePhase.materialSelection')}
                    </div>
                    <ShadeSelector
                        name="materialSelection"
                        options={['EW', 'BW', 'HV', 'LV', 'BWM', 'HVM', 'LVM', 'HW']}
                        required
                    />
                    <FieldError name="materialSelection" />
                </div>
            )}
            {(isVitaGuide || shadeGuide === 'ipsEmax') && (
                <div className="restorative-shade-guide">
                    <div className="restorative-subquestion restorative-shade-label">
                        {get('@PatientInfo.smileDesign.restorativePhase.prepStumpShade')}
                    </div>
                    <RestorativeTextField
                        name="currentShade"
                        placeholderKey="@PatientInfo.smileDesign.restorativePhase.shadeInputPlaceholder"
                        required
                    />
                    <FieldError name="currentShade" />
                    <div className="restorative-subquestion restorative-shade-label">
                        {get('@PatientInfo.smileDesign.restorativePhase.targetShade')}
                    </div>
                    <ShadeSelector
                        name="targetShade"
                        options={isVitaGuide ? VITA_GUIDE_SHADE_OPTIONS : IPS_EMAX_GUIDE_SHADE_OPTIONS}
                        optionGroups={isVitaGuide ? VITA_GUIDE_SHADE_GROUPS : IPS_EMAX_GUIDE_SHADE_GROUPS}
                        required
                    />
                    <FieldError name="targetShade" />
                    {isVitaGuide && (
                        <>
                            <div className="restorative-subquestion restorative-shade-label">
                                {get('@PatientInfo.smileDesign.restorativePhase.options.others')}
                            </div>
                            <RestorativeTextField
                                name="otherShade"
                                placeholderKey="@PatientInfo.smileDesign.restorativePhase.otherShadePlaceholder"
                            />
                        </>
                    )}
                </div>
            )}
            <div className="restorative-question-title">
                4. {get('@PatientInfo.smileDesign.restorativePhase.translucencyLevel')} <b>*</b>
            </div>
            <RadioCardGroup
                name="translucencyLevel"
                required
                options={[
                    { value: 'low', label: get('@PatientInfo.smileDesign.restorativePhase.options.low') },
                    { value: 'medium', label: get('@PatientInfo.smileDesign.restorativePhase.options.medium') },
                    { value: 'high', label: get('@PatientInfo.smileDesign.restorativePhase.options.high') },
                ]}
            />
            <FieldError name="translucencyLevel" />
            <div className="restorative-question-title">
                5. {get('@PatientInfo.smileDesign.restorativePhase.surfaceTexture')} <b>*</b>
            </div>
            <RadioCardGroup
                name="surfaceTexture"
                required
                options={[
                    { value: 'smooth', label: get('@PatientInfo.smileDesign.restorativePhase.options.smooth') },
                    { value: 'medium', label: get('@PatientInfo.smileDesign.restorativePhase.options.medium') },
                    { value: 'pronounced', label: get('@PatientInfo.smileDesign.restorativePhase.options.pronounced') },
                ]}
            />
            <FieldError name="surfaceTexture" />
            <div className="restorative-question-title">
                6. {get('@PatientInfo.smileDesign.restorativePhase.surfaceFinishing')} <b>*</b>
            </div>
            <RadioCardGroup
                name="surfaceFinishingCharacterization"
                required
                options={[
                    { value: 'fullGlazing', label: get('@PatientInfo.smileDesign.restorativePhase.options.fullGlazing') },
                    {
                        value: 'stainingGlazing',
                        label: get('@PatientInfo.smileDesign.restorativePhase.options.stainingGlazing'),
                        description: get('@PatientInfo.smileDesign.restorativePhase.options.stainingGlazingDescription'),
                    },
                ]}
            />
            <FieldError name="surfaceFinishingCharacterization" />
            {surfaceFinishing === 'stainingGlazing' && (
                <div className="restorative-effects">
                    <div className="restorative-question-title">
                        {get('@PatientInfo.smileDesign.restorativePhase.effectsToReproduce')}{' '}
                        <span>({get('@PatientInfo.smileDesign.restorativePhase.selectAll')})</span>
                    </div>
                    <CheckboxGroup
                        name="effectsToReproduce"
                        card={false}
                        options={[
                            { value: 'Increased Cervical Chroma / Saturation', label: get('@PatientInfo.smileDesign.restorativePhase.options.increasedCervicalChroma') },
                            { value: 'White Spots', label: get('@PatientInfo.smileDesign.restorativePhase.options.whiteSpots') },
                            { value: 'Craze Lines / Crack-Line Effects', label: get('@PatientInfo.smileDesign.restorativePhase.options.crazeLines') },
                            { value: 'Incisal Translucency', label: get('@PatientInfo.smileDesign.restorativePhase.options.incisalTranslucency') },
                            { value: 'Opalescence Effects', label: get('@PatientInfo.smileDesign.restorativePhase.options.opalescenceEffects') },
                            { value: 'Halo Effects', label: get('@PatientInfo.smileDesign.restorativePhase.options.haloEffects') },
                            { value: 'Other custom characterization', label: get('@PatientInfo.smileDesign.restorativePhase.options.otherCharacterization') },
                        ]}
                    />
                </div>
            )}
            <div className="restorative-question-title">
                7. {get('@PatientInfo.smileDesign.restorativePhase.cementationTray')}{' '}
                <span>({get('@PatientInfo.smileDesign.restorativePhase.cementationTrayOptional')})</span>
            </div>
            <RadioCardGroup
                name="cementationTray"
                defaultValue="no"
                card={false}
                options={[
                    { value: 'yes', label: get('@PatientInfo.smileDesign.restorativePhase.options.yes') },
                    { value: 'no', label: get('@PatientInfo.smileDesign.restorativePhase.options.no') },
                ]}
            />
            <div className="restorative-question-title">
                8. {get('@PatientInfo.smileDesign.restorativePhase.designSpecifications')} <b>*</b>
            </div>
            <Controller
                name="designSpecifications"
                control={control}
                defaultValue=""
                rules={{ required: 'Required' }}
                render={({ field }) => (
                    <TextField
                        {...field}
                        className="restorative-textarea"
                        placeholder={get('@PatientInfo.smileDesign.restorativePhase.shapeIncisalEdgeAlignment')}
                        multiline
                        rows={4}
                        variant="outlined"
                    />
                )}
            />
            <FieldError name="designSpecifications" />
            <RestorativeOptionalSection
                appliances={FIT_APPLIANCES.map((value) => ({
                    value,
                    label: get(`@PatientInfo.smileDesign.restorativePhase.options.${{
                        'Bleaching retainer': 'bleachingRetainer',
                        'Ecodont combi retainer': 'ecodontCombiRetainer',
                        'Ecodont® Retainer with Veneer Shield': 'veneerRetainer',
                        'Veneer retainer': 'veneerRetainer',
                        'Ecodont retainer': 'ecodontRetainer',
                        'Invisible retainer': 'invisibleRetainer',
                        Splint: 'splint',
                    }[value]}`),
                }))}
                startNumber={9}
            />
        </>
    );
};

/** Renders all IPS e.max Guided Veneer restoration detail fields. */
const GuidedDetails = ({ get }) => {
    const { control, setValue, watch } = useFormContext();
    const materialPreference = watch('materialPreference');
    const surfaceFinishing = watch('surfaceFinishingCharacterization');

    /** Clears effects when staining and glazing is no longer selected. */
    useEffect(() => {
        if (surfaceFinishing !== 'stainingGlazing') setValue('effectsToReproduce', []);
    }, [setValue, surfaceFinishing]);

    return (
        <>
            <div className="restorative-section-heading">
                {get('@PatientInfo.smileDesign.restorativePhase.sectionTwo')}
            </div>
            <div className="restorative-product-heading">
                {get('@PatientInfo.smileDesign.restorativePhase.ceramicVeneersGuidedDesc')}
            </div>
            <div className="restorative-question-title">
                2. {get('@PatientInfo.smileDesign.restorativePhase.materialChoice')} <b>*</b>
            </div>
            <RadioCardGroup
                name="materialPreference"
                required
                options={[
                    { value: 'ipsEmax', label: get('@PatientInfo.smileDesign.restorativePhase.options.ipsEmax') },
                    { value: 'layered', label: get('@PatientInfo.smileDesign.restorativePhase.options.ipsEmaxLayered') },
                ]}
            />
            <FieldError name="materialPreference" />
            <div className="restorative-question-title">
                3. {get('@PatientInfo.smileDesign.restorativePhase.shade')} <b>*</b>
            </div>
            {!materialPreference && (
                <div className="restorative-hint">
                    {get('@PatientInfo.smileDesign.restorativePhase.shadeHint')}
                </div>
            )}
            {materialPreference && (
                <>
                    <div className="restorative-subquestion restorative-shade-label">
                        {get('@PatientInfo.smileDesign.restorativePhase.currentToothShade')}
                    </div>
                    <ShadeSelector name="currentShade" optionGroups={VITA_SHADE_GROUPS} required />
                    <FieldError name="currentShade" />
                    <div className="restorative-subquestion restorative-shade-label">
                        {get('@PatientInfo.smileDesign.restorativePhase.desiredFinalShade')}
                    </div>
                    <ShadeSelector name="targetShade" optionGroups={VITA_SHADE_GROUPS} required />
                    <FieldError name="targetShade" />
                </>
            )}
            <div className="restorative-question-title">
                4. {get('@PatientInfo.smileDesign.restorativePhase.translucencyLevel')} <b>*</b>
            </div>
            <RadioCardGroup
                name="translucencyLevel"
                required
                options={[
                    { value: 'low', label: get('@PatientInfo.smileDesign.restorativePhase.options.low') },
                    { value: 'medium', label: get('@PatientInfo.smileDesign.restorativePhase.options.medium') },
                    { value: 'high', label: get('@PatientInfo.smileDesign.restorativePhase.options.high') },
                ]}
            />
            <FieldError name="translucencyLevel" />
            <div className="restorative-question-title">
                5. {get('@PatientInfo.smileDesign.restorativePhase.surfaceTexture')} <b>*</b>
            </div>
            <RadioCardGroup
                name="surfaceTexture"
                required
                options={[
                    { value: 'smooth', label: get('@PatientInfo.smileDesign.restorativePhase.options.smooth') },
                    { value: 'medium', label: get('@PatientInfo.smileDesign.restorativePhase.options.medium') },
                    { value: 'pronounced', label: get('@PatientInfo.smileDesign.restorativePhase.options.pronounced') },
                ]}
            />
            <FieldError name="surfaceTexture" />
            <div className="restorative-question-title">
                6. {get('@PatientInfo.smileDesign.restorativePhase.surfaceFinishing')} <b>*</b>
            </div>
            <RadioCardGroup
                name="surfaceFinishingCharacterization"
                required
                options={[
                    { value: 'fullGlazing', label: get('@PatientInfo.smileDesign.restorativePhase.options.fullGlazing') },
                    {
                        value: 'stainingGlazing',
                        label: get('@PatientInfo.smileDesign.restorativePhase.options.stainingGlazing'),
                        description: get('@PatientInfo.smileDesign.restorativePhase.options.stainingGlazingDescription'),
                    },
                ]}
            />
            <FieldError name="surfaceFinishingCharacterization" />
            {surfaceFinishing === 'stainingGlazing' && (
                <div className="restorative-effects">
                    <div className="restorative-question-title">
                        {get('@PatientInfo.smileDesign.restorativePhase.effectsToReproduce')}{' '}
                        <span>({get('@PatientInfo.smileDesign.restorativePhase.selectAll')})</span>
                    </div>
                    <CheckboxGroup
                        name="effectsToReproduce"
                        card={false}
                        options={[
                            { value: 'Increased Cervical Chroma / Saturation', label: get('@PatientInfo.smileDesign.restorativePhase.options.increasedCervicalChroma') },
                            { value: 'White Spots', label: get('@PatientInfo.smileDesign.restorativePhase.options.whiteSpots') },
                            { value: 'Craze Lines / Crack-Line Effects', label: get('@PatientInfo.smileDesign.restorativePhase.options.crazeLines') },
                            { value: 'Incisal Translucency', label: get('@PatientInfo.smileDesign.restorativePhase.options.incisalTranslucency') },
                            { value: 'Opalescence Effects', label: get('@PatientInfo.smileDesign.restorativePhase.options.opalescenceEffects') },
                            { value: 'Halo Effects', label: get('@PatientInfo.smileDesign.restorativePhase.options.haloEffects') },
                            { value: 'Other custom characterization', label: get('@PatientInfo.smileDesign.restorativePhase.options.otherCharacterization') },
                        ]}
                    />
                </div>
            )}
            <div className="restorative-question-title">
                7. {get('@PatientInfo.smileDesign.restorativePhase.designSpecifications')} <b>*</b>
            </div>
            <Controller
                name="designSpecifications"
                control={control}
                defaultValue=""
                rules={{ required: 'Required' }}
                render={({ field }) => (
                    <TextField
                        {...field}
                        className="restorative-textarea"
                        placeholder={get('@PatientInfo.smileDesign.restorativePhase.shapeIncisalEdgeAlignment')}
                        multiline
                        rows={4}
                        variant="outlined"
                    />
                )}
            />
            <FieldError name="designSpecifications" />
            <RestorativeOptionalSection
                appliances={FIT_APPLIANCES.map((value) => ({
                    value,
                    label: get(`@PatientInfo.smileDesign.restorativePhase.options.${{
                        'Bleaching retainer': 'bleachingRetainer',
                        'Ecodont combi retainer': 'ecodontCombiRetainer',
                        'Ecodont® Retainer with Veneer Shield': 'veneerRetainer',
                        'Veneer retainer': 'veneerRetainer',
                        'Ecodont retainer': 'ecodontRetainer',
                        'Invisible retainer': 'invisibleRetainer',
                        Splint: 'splint',
                    }[value]}`),
                }))}
                startNumber={8}
            />
        </>
    );
};

/** Renders the conditional prescription details for the selected Q1 value. */
const PrescriptionDetails = ({ get }) => {
    const { control, getValues, setValue } = useFormContext();
    const plannedRestorationSolution = useWatch({ control, name: 'plannedRestorationSolution' });
    const previousSolution = useRef(plannedRestorationSolution);

    useEffect(() => {
        if (previousSolution.current && previousSolution.current !== plannedRestorationSolution) {
            [
                'orthodonticPhase',
                'injectionLayers',
                'materialPreference',
                'subMaterialPreference',
                'specifiedMaterialIfAny',
                'shadeGuide',
                'targetShade',
                'otherShade',
                'currentShade',
                'translucencyLevel',
                'surfaceTexture',
                'surfaceFinishingCharacterization',
                'cementationTray',
                'designSpecifications',
                'restorationRemark',
            ].forEach((name) => setValue(name, ''));
            [
                'fitType',
                'nanoFilledInjectableCompositeShade',
                'materialSelection',
                'effectsToReproduce',
                'optionalAppliances',
            ].forEach((name) => setValue(name, []));
            setValue('additionalSyringesIncreasedOpacity', []);
        }
        if (plannedRestorationSolution === 'option.ceramicVeneersCrowns' && !getValues('cementationTray')) {
            setValue('cementationTray', 'no');
        }
        if (SOLUTIONS_WITH_FINISHING_DEFAULTS.includes(plannedRestorationSolution)) {
            Object.entries(FINISHING_QUESTION_DEFAULTS).forEach(([fieldName, defaultValue]) => {
                if (!getValues(fieldName)) setValue(fieldName, defaultValue);
            });
        }
        previousSolution.current = plannedRestorationSolution;
    }, [getValues, plannedRestorationSolution, setValue]);

    if (FIT_VALUES.includes(plannedRestorationSolution)) {
        return <FitDetails get={get} plannedRestorationSolution={plannedRestorationSolution} />;
    }
    if (plannedRestorationSolution === 'option.ceramicVeneersCrowns') return <CeramicDetails get={get} />;
    if (plannedRestorationSolution === 'option.guidedVeneerTreatment') return <GuidedDetails get={get} />;
    return null;
};

/** Renders the Restorative Phase Case Intake workflow. */
const RestorativePhaseCaseIntake = () => {
    const history = useHistory();
    const location = useLocation();
    const { id: caseId } = useParams();
    const dispatch = useDispatch();
    const get = useLang();
    const progressBarRef = useRef(null);
    const uploadFile = useSelector((state) => state.uploadFile);
    const clinicalApprovementTreatmentPlanData = useSelector(
        (state) => state.smileDesign.clinicalApprovementTreatmentPlanData
    );
    const displayFirstFitFlag = useSelector((state) => state.globalReducer.displayFirstFitFlag);
    const [activeStep, setActiveStep] = useState('mockup');
    const [isLoading, setIsLoading] = useState(true);
    const [pendingRestorationSolution, setPendingRestorationSolution] = useState('');
    const [isRestorationSolutionConfirmOpen, setIsRestorationSolutionConfirmOpen] = useState(false);
    const [hasMockupOrder, setHasMockupOrder] = useState(true);
    const [isToothShapeRequired, setIsToothShapeRequired] = useState(null);
    const isMarieGroup = useMemo(() => JSON.parse(localStorage.getItem('isMarieGroup')), []);
    const isTWGroup = useMemo(() => JSON.parse(localStorage.getItem('isTWGroup')), []);
    const methods = useForm({
        mode: 'onChange',
        defaultValues: { mockupFeedback: '', plannedRestorationSolution: '' },
    });
    const mockupFeedback = useWatch({ control: methods.control, name: 'mockupFeedback' });
    const plannedRestorationSolution = useWatch({ control: methods.control, name: 'plannedRestorationSolution' });
    const formValues = useWatch({ control: methods.control });
    const patientId = location.state?.patientId || caseId;
    const treatmentTypes = location.state?.treatmentTypes;
    const q1Options = useMemo(
        () =>
            (displayFirstFitFlag ? smilDesignQuestionsConfigFirstFit : smilDesignQuestionsConfig)(
                undefined,
                isMarieGroup,
                false,
                isTWGroup
            )[1].option,
        [displayFirstFitFlag, isMarieGroup, isTWGroup]
    );
    const workflowSteps = useMemo(
        () => (hasMockupOrder ? STEPS : STEPS.filter((step) => step.id !== 'mockup')),
        [hasMockupOrder]
    );

    useEffect(() => {
        if (['DR'].includes(JSON.parse(localStorage.getItem('role')))) {
            dispatch(fetchConnectPlatformData());
        }
    }, [dispatch]);

    useEffect(() => {
        preloadShadeImages();
    }, []);

    /** Gets the display label for the pending Prescription Notes Q1 option. */
    const pendingRestorationSolutionLabel = useMemo(() => {
        const selectedOption = q1Options.find((option) => option.value === pendingRestorationSolution);
        return selectedOption ? get(selectedOption.label) : '';
    }, [get, pendingRestorationSolution, q1Options]);

    /** Returns whether a Prescription Notes detail field already has an answer. */
    const hasPrescriptionDetailAnswers = () =>
        PRESCRIPTION_DETAIL_FIELD_NAMES.some((fieldName) => {
            const value = methods.getValues(fieldName);
            if (Array.isArray(value)) return value.length > 0;
            if (value && typeof value === 'object') return Object.keys(value).length > 0;
            return Boolean(value?.trim ? value.trim() : value);
        });

    /** Applies the selected Prescription Notes Q1 value after confirmation when required. */
    const applyRestorationSolution = (value) => {
        methods.setValue('plannedRestorationSolution', value, { shouldValidate: true });
        setPendingRestorationSolution('');
    };

    /** Requests confirmation before replacing answered Prescription Notes details. */
    const handleRestorationSolutionChange = (value) => {
        if (value === plannedRestorationSolution) return;

        if (hasPrescriptionDetailAnswers()) {
            setPendingRestorationSolution(value);
            setIsRestorationSolutionConfirmOpen(true);
            return;
        }

        applyRestorationSolution(value);
    };

    /** Loads the initial case data before showing the restorative workflow. */
    useEffect(() => {
        let isMounted = true;
        async function loadInitialCaseData() {
            try {
                const [prescriptionResponse, mockupResponse, impressionResponse] = await Promise.all([
                    safeGet(`/case/smileDesignPrescriptionNotes/${caseId}`),
                    safeGet(`/case/mockup_order/exists/${caseId}`),
                    safeGet(`/case/restorationForm/impressionRequired/${caseId}`),
                ]);
                if (!isMounted) return;
                methods.setValue('plannedRestorationSolution', prescriptionResponse?.result?.plannedRestorationSolution || '', {
                    shouldValidate: true,
                });
                if (mockupResponse?.success) setHasMockupOrder(mockupResponse.hasMockupOrder !== false);
                setIsToothShapeRequired(
                    impressionResponse?.result.isImpressionRequired === true &&
                        Array.isArray(treatmentTypes) &&
                        treatmentTypes.includes('TrioClear')
                );
            } finally {
                if (isMounted) setIsLoading(false);
            }
        }
        loadInitialCaseData();
        return () => {
            isMounted = false;
        };
    }, [caseId, methods, treatmentTypes]);

    /** Selects the first available step when mock-up feedback is not applicable. */
    useEffect(() => {
        if (!hasMockupOrder && activeStep === 'mockup') setActiveStep('prescription');
    }, [activeStep, hasMockupOrder]);

    /** Displays scan guidance when entering Impression with Guided Veneer selected. */
    useEffect(() => {
        if (activeStep === 'impression' && plannedRestorationSolution === 'option.guidedVeneerTreatment') {
            dispatch(
                setDialog({
                    title: '@NewPatient.warning.guidedVeneerUploadScanGuidanceTitle',
                    content: '@NewPatient.warning.guidedVeneerUploadScanGuidanceContent',
                })
            );
        }
    }, [activeStep, dispatch, plannedRestorationSolution]);

    /** Returns to the current patient page. */
    const handleBack = () => history.push(`/patients/${caseId}`);

    /** Returns the required fields for the active Prescription Notes branch. */
    const getPrescriptionFieldNames = () => {
        if (FIT_VALUES.includes(plannedRestorationSolution)) {
            return [
                'plannedRestorationSolution',
                // 'orthodonticPhase',
                'injectionLayers',
                'nanoFilledInjectableCompositeShade',
            ];
        }
        if (plannedRestorationSolution === 'option.ceramicVeneersCrowns') {
            const shadeGuide = methods.getValues('shadeGuide');
            const subMaterialPreference = methods.getValues('subMaterialPreference');
            const shadeFields =
                shadeGuide === 'ezneer'
                    ? ['currentShade', 'targetShade', 'materialSelection']
                    : ['currentShade', 'targetShade'];
            return [
                'plannedRestorationSolution',
                'materialPreference',
                'subMaterialPreference',
                ...(['otherZirconia', 'otherLithiumDisilicate'].includes(subMaterialPreference)
                    ? ['specifiedMaterialIfAny']
                    : []),
                'shadeGuide',
                ...shadeFields,
                'translucencyLevel',
                'surfaceTexture',
                'surfaceFinishingCharacterization',
                'designSpecifications',
            ];
        }
        if (plannedRestorationSolution === 'option.guidedVeneerTreatment') {
            return [
                'plannedRestorationSolution',
                'translucencyLevel',
                'currentShade',
                'targetShade',
                'materialPreference',
                'surfaceTexture',
                'surfaceFinishingCharacterization',
                'designSpecifications',
            ];
        }
        return ['plannedRestorationSolution'];
    };

    /** Moves between steps without preventing access for incomplete required fields. */
    const handleStepMove = (direction) => {
        const currentIndex = workflowSteps.findIndex((step) => step.id === activeStep);
        setActiveStep(workflowSteps[currentIndex + direction].id);
    };

    /** Determines whether a workflow step has unanswered required fields. */
    const isStepIncomplete = (stepId) => {
        if (stepId === 'mockup')
            return (
                hasMockupOrder &&
                (!formValues?.mockupFeedback ||
                (formValues.mockupFeedback === 'adjustmentRequired' && !formValues.mockupFeedbackRemark?.trim())
                )
            );
        if (stepId === 'impression') {
            const toothShapeFiles = formValues?.restorativeImpression?.toothShape?.files;
            const hasMissingToothShapeFile =
                isToothShapeRequired === true &&
                ['tooth_shape_upper', 'tooth_shape_lower', 'tooth_shape_bite'].some(
                    (fileId) => !toothShapeFiles?.find((file) => file.id === fileId)?.data
                );
            return hasMissingToothShapeFile;
        }
        if (!formValues?.plannedRestorationSolution) return true;
        if (FIT_VALUES.includes(formValues.plannedRestorationSolution))
            return (
                // !formValues.orthodonticPhase ||
                !formValues.injectionLayers ||
                (formValues.injectionLayers === 'twoLayers'
                    ? formValues.nanoFilledInjectableCompositeShade?.length !== 2 || ![
                          formValues.nanoFilledInjectableCompositeShade?.some((shade) => shade?.layer === 'enamel'),
                          formValues.nanoFilledInjectableCompositeShade?.some((shade) => shade?.layer === 'dentin'),
                      ].every(Boolean)
                    : formValues.nanoFilledInjectableCompositeShade?.length !== 1 || !formValues.nanoFilledInjectableCompositeShade?.some(
                          (shade) => shade?.layer === 'enamelOrDentin' && shade?.option
                      ))
            );
        if (formValues.plannedRestorationSolution === 'option.ceramicVeneersCrowns') {
            if (
                !formValues.materialPreference ||
                !formValues.subMaterialPreference ||
                !formValues.shadeGuide ||
                !formValues.translucencyLevel ||
                !formValues.surfaceTexture ||
                !formValues.surfaceFinishingCharacterization ||
                !formValues.designSpecifications?.trim()
            )
                return true;
            if (
                ['otherZirconia', 'otherLithiumDisilicate'].includes(formValues.subMaterialPreference) &&
                !formValues.specifiedMaterialIfAny?.trim()
            )
                return true;
            return formValues.shadeGuide === 'ezneer'
                ? !formValues.currentShade?.length || !formValues.targetShade?.length || !formValues.materialSelection?.length
                : !formValues.currentShade?.length || !formValues.targetShade?.length;
        }
        if (formValues.plannedRestorationSolution === 'option.guidedVeneerTreatment')
            return (
                !formValues.translucencyLevel ||
                !formValues.currentShade?.length ||
                !formValues.targetShade?.length ||
                !formValues.materialPreference ||
                !formValues.surfaceTexture ||
                !formValues.surfaceFinishingCharacterization ||
                !formValues.designSpecifications?.trim()
            );
        return false;
    };

    /** Stores the final form data with the validated impression upload. */
    const handleSubmit = async () => {
        const mockupValid = hasMockupOrder ? await methods.trigger(['mockupFeedback', 'mockupFeedbackRemark']) : true;
        const prescriptionValid = await methods.trigger(getPrescriptionFieldNames());
        if (
            !mockupValid ||
            !prescriptionValid ||
            (hasMockupOrder && isStepIncomplete('mockup')) ||
            isStepIncomplete('prescription')
        ) {
            dispatch(setDialogMessage(dialogData.allMandatory.content));
            return;
        }
        try {
            setIsLoading(true);
            const impressionData = await uploadFile.handleImpressionData();
            if (!impressionData) {
                dispatch(setDialogMessage(dialogData.allMandatory.content));
                return;
            }
            const form = impressionData.impressionFile;
            const data = methods.getValues();
            if (!hasMockupOrder) {
                delete data.mockupFeedback;
                delete data.mockupFeedbackRemark;
            }
            form.append(
                'body',
                JSON.stringify({
                    restorationForm: { ...data, isMarieGroup },
                    ...(impressionData.case_impression && { case_impression: impressionData.case_impression }),
                    ...(impressionData.mockup_feedback_files && {
                        mockup_feedback_files: impressionData.mockup_feedback_files,
                    }),
                    approvementOption:
                        clinicalApprovementTreatmentPlanData === '' ? null : clinicalApprovementTreatmentPlanData,
                })
            );
            const result = await instance.post(`case/restorationForm/${caseId}`, form, {
                onUploadProgress: progressBarRef.current?.onProgressChange,
            });
            if (result.data.success) {
                dispatch(easySnackbar(get('@PatientInfo.smileDesign.approveRestorationPhaseOrder'), 'success', true));
                history.replace(`/patients/${caseId}`);
            }
        } catch (error) {
            dispatch(easySnackbar(error?.response?.data?.message, 'error'));
        } finally {
            setIsLoading(false);
        }
    };

    /** Renders the content assigned to the active workflow step. */
    const renderStepContent = () => {
        if (activeStep === 'mockup' && hasMockupOrder) {
            return <MockupFeedbackStep control={methods.control} get={get} mockupFeedback={mockupFeedback} />;
        }
        if (activeStep === 'prescription') {
            return (
                <PrescriptionNoteStep
                    get={get}
                    q1Options={q1Options}
                    onRestorationSolutionChange={handleRestorationSolutionChange}
                    RadioCardGroup={RadioCardGroup}
                    PrescriptionDetails={PrescriptionDetails}
                    FieldError={FieldError}
                />
            );
        }
        return <ImpressionStep caseId={caseId} get={get} hasMockupOrder={hasMockupOrder} isToothShapeRequired={isToothShapeRequired} />;
    };

    return (
        <FormProvider {...methods}>
            <div className={`${styles.page} restorative-phase-scope restorative-phase-radio-scope`}>
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
                    <Link to="/" style={{ color: 'black' }}>
                        Case Management
                    </Link>
                    <Link to={`/patients/${caseId}`} style={{ color: 'black' }}>{`Patient #${patientId}`}</Link>
                    <Typography>{get('@PatientInfo.smileDesign.restorativePhase.caseIntakeTitle')}</Typography>
                </Breadcrumbs>
                <Button variant="contained" className={styles.back} onClick={handleBack}>
                    Back
                </Button>
                <div className={styles.steps}>
                    {workflowSteps.map((step, index) => {
                        const Icon = step.Icon;
                        const isActive = activeStep === step.id;
                        const isIncomplete = isStepIncomplete(step.id);
                        return (
                            <React.Fragment key={step.id}>
                                <div
                                    className={`${styles.step} ${isActive ? styles.stepActive : ''}`}
                                    onClick={() => setActiveStep(step.id)}
                                >
                                    <div className={newPatientTabsStyle.CustomBadgeContainer}>
                                        {isIncomplete && <span className={newPatientTabsStyle.CustomBadge}>*</span>}
                                        <IconButton className={isActive ? styles.stepIconActive : styles.stepIcon}>
                                            <Icon />
                                        </IconButton>
                                    </div>
                                    <div className={styles.stepLabel}>{get(step.labelKey)}</div>
                                </div>
                                {index < workflowSteps.length - 1 && <KeyboardArrowRightIcon className={styles.arrow} />}
                            </React.Fragment>
                        );
                    })}
                </div>
                <div>{renderStepContent()}</div>
                <div className={newPatientButtonStyle.customButton}>
                    {activeStep !== workflowSteps[0].id && (
                        <button type="button" className={newPatientButtonStyle.previous} onClick={() => handleStepMove(-1)}>
                            {get('@NewPatient.navBtns.previous')}
                        </button>
                    )}
                    {activeStep !== workflowSteps[workflowSteps.length - 1].id ? (
                        <button type="button" className={newPatientButtonStyle.next} onClick={() => handleStepMove(1)}>
                            {get('@NewPatient.navBtns.next')}
                        </button>
                    ) : (
                        <button type="button" className={newPatientButtonStyle.next} onClick={handleSubmit}>
                            {get('@NewPatient.navBtns.submit')}
                        </button>
                    )}
                </div>
            </div>
            <PercentageProgressRing ref={progressBarRef} open={isLoading} label={undefined} />
            <ErrorDialog />
            <ConfirmDialog
                title={get('@PatientInfo.smileDesign.restorativePhase.ChangeRestorationSolution')}
                open={isRestorationSolutionConfirmOpen}
                setOpen={setIsRestorationSolutionConfirmOpen}
                onConfirm={() => applyRestorationSolution(pendingRestorationSolution)}
            >
               {`This solution was carried over from the approved smile design. Changing it to ${pendingRestorationSolutionLabel} will clear the answers below.`}
            </ConfirmDialog>
        </FormProvider>
    );
};

export default RestorativePhaseCaseIntake;
